import { useEffect, useRef } from "react";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { useTranslation } from "react-i18next";
import i18n from "../../lib/i18n";

const GA_ID = "G-VYR0W8MNMY";

function loadGoogleAnalytics() {
  if (document.getElementById("ga-script")) return;

  window.dataLayer = window.dataLayer || [];
  // Must use `arguments` object (not rest params) - gtag.js expects this specific format
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID);

  const script = document.createElement("script");
  script.id = "ga-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
}

function disableGoogleAnalytics() {
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = true;
}

function getTranslations(lang: string) {
  return {
    consentModal: {
      title: i18n.t("cookieConsent.title", { lng: lang }),
      description: `${i18n.t("cookieConsent.description", { lng: lang })} <a href="/privacy-policy">${i18n.t("cookieConsent.privacyPolicyLink", { lng: lang })}</a>`,
      acceptAllBtn: i18n.t("cookieConsent.acceptAll", { lng: lang }),
      acceptNecessaryBtn: i18n.t("cookieConsent.acceptNecessary", {
        lng: lang,
      }),
      showPreferencesBtn: i18n.t("cookieConsent.showPreferences", {
        lng: lang,
      }),
    },
    preferencesModal: {
      title: i18n.t("cookieConsent.preferencesTitle", { lng: lang }),
      acceptAllBtn: i18n.t("cookieConsent.acceptAll", { lng: lang }),
      acceptNecessaryBtn: i18n.t("cookieConsent.acceptNecessary", {
        lng: lang,
      }),
      savePreferencesBtn: i18n.t("cookieConsent.savePreferences", {
        lng: lang,
      }),
      closeIconLabel: i18n.t("cookieConsent.closeModal", { lng: lang }),
      sections: [
        {
          title: i18n.t("cookieConsent.preferencesTitle", { lng: lang }),
          description: i18n.t("cookieConsent.preferencesDescription", {
            lng: lang,
          }),
        },
        {
          title: i18n.t("cookieConsent.necessary.title", { lng: lang }),
          description: i18n.t("cookieConsent.necessary.description", {
            lng: lang,
          }),
          linkedCategory: "necessary",
        },
        {
          title: i18n.t("cookieConsent.analytics.title", { lng: lang }),
          description: i18n.t("cookieConsent.analytics.description", {
            lng: lang,
          }),
          linkedCategory: "analytics",
        },
      ],
    },
  };
}

export function CookieConsentBanner() {
  const { i18n: i18nHook } = useTranslation();
  const initialized = useRef(false);

  // Initialize cookie consent once on mount
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // Get initial language from the i18n instance directly
    const initialLang = i18n.language.startsWith("fr") ? "fr" : "en";

    CookieConsent.run({
      guiOptions: {
        consentModal: {
          layout: "box inline",
          position: "bottom left",
        },
        preferencesModal: {
          layout: "box",
        },
      },

      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          enabled: false,
          readOnly: false,
        },
      },

      language: {
        default: initialLang,
        translations: {
          en: getTranslations("en"),
          fr: getTranslations("fr"),
        },
      },

      onConsent: () => {
        if (CookieConsent.acceptedCategory("analytics")) {
          loadGoogleAnalytics();
        } else {
          disableGoogleAnalytics();
        }
      },

      onChange: () => {
        if (CookieConsent.acceptedCategory("analytics")) {
          loadGoogleAnalytics();
        } else {
          disableGoogleAnalytics();
        }
      },
    });
  }, []);

  // Update language when i18n language changes (without resetting preferences)
  useEffect(() => {
    if (!initialized.current) return;
    const currentLang = i18nHook.language.startsWith("fr") ? "fr" : "en";
    CookieConsent.setLanguage(currentLang);
  }, [i18nHook.language]);

  return null; // This component doesn't render anything, it just initializes the cookie consent
}
